<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterConsumption',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    meter: {
      type: Object,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getPayload = (...args) => {
    const _item = item.value;
    return _.extend(_item, {
      meter: _item.meter ? { id: _item.meter.id } : null,
      unit_price:
        _item.unit_price && _item.unit_price.amount != null
          ? _item.unit_price
          : null,
    });
  };

  const item = ref(null);
  const currencies = ref(null);
  const units = ref(null);
  const formName = ref('meterConsumption');
  const formId = ref(null);
  const labelAlign = ref('right');
  const isReadOnly = ref(null);
  const tariffs = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_4152404563918957_dataSource = computed(() => ({
    name: 'suggest_rental',
  }));

  const $data = computed(() => ({
    item: item.value,
    currencies: currencies.value,
    units: units.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    isReadOnly: isReadOnly.value,
    tariffs: tariffs.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_4152404563918957_dataSource: b_4152404563918957_dataSource.value,
  }));

  const $$load_item = () => {
    return props.oid
      ? $fn.value
          .fetch('read_meter_consumption', { parameters: { id: props.oid } })
          .then((p) => {
            if (p.unit_price == null) {
              p.unit_price = { amount: null, currency: null };
            }
            return p;
          })
      : $fn.value.schemaDefaults('CreateMeterConsumption', {
          meter: props.meter || null,
          unit: props.meter?.unit || null,
          unit_price: { amount: null, currency: null },
        });
  };
  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_units = () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_isReadOnly = () => {
    return item.value.locked || item.value.isReadonly;
  };
  const $$load_tariffs = () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'currencies' },
      { name: 'units' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isReadOnly', dependsOn: ['item'] },
      { name: 'tariffs' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_4152404563918957_dataSource', nodeId: '4152404563918957' },
    ],
    loaders: {
      item: $$load_item,
      currencies: $$load_currencies,
      units: $$load_units,
      formId: $$load_formId,
      isReadOnly: $$load_isReadOnly,
      tariffs: $$load_tariffs,
    },
    ux: {},
    dataRefs: {
      item,
      currencies,
      units,
      formName,
      formId,
      labelAlign,
      isReadOnly,
      tariffs,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_4152404563918957_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: props.oid
        ? $fn.value.gettext('Edit Meter Consumption')
        : $fn.value.gettext('Add Meter Consumption'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_rbo1ac2yh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_4sbugn4tk_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_wdxdmge9v_getActionArgs = async (
    $event
  ) => {
    return {
      operation: item.value.id
        ? 'update_meter_consumption'
        : 'create_meter_consumption',
      data: getPayload(),
    };
  };
  const act_3322283983417540_closeModal_hhpb4btyj_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_5419443714917073_closeModal_flsr0wuz8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2167826738365259_closeModal_7ynx7vlg3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_rbo1ac2yh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_rbo1ac2yh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_pwu87a2ns =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_rbo1ac2yh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_pwu87a2ns],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_s15xu8n0j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_l4093wc4j =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_s15xu8n0j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_l4093wc4j],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_oms2tx6oj =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_lcsx4xbjk =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_oms2tx6oj,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_lcsx4xbjk],
    event: 'close',
  });
  const act_1040167445267876_closeModal_4sbugn4tk =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_4sbugn4tk_getActionArgs,
    });
  const inst_evh_1040167445267876_success_31vnplytf =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_4sbugn4tk,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_wdxdmge9v = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_wdxdmge9v_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_31vnplytf],
  });
  const inst_evh_1040167445267876_submit_k4lr498x7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_wdxdmge9v,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_k4lr498x7],
    event: 'submit',
  });
  const act_3322283983417540_closeModal_hhpb4btyj =
    new actionTypes.CloseModalAction({
      actionArgs: act_3322283983417540_closeModal_hhpb4btyj_getActionArgs,
    });
  const inst_evh_3322283983417540_success_1x9pwdg5v =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_closeModal_hhpb4btyj,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_3322283983417540_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3322283983417540_success_1x9pwdg5v],
    event: 'success',
  });
  const act_5419443714917073_closeModal_flsr0wuz8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_5419443714917073_closeModal_flsr0wuz8_getActionArgs,
    });
  const inst_evh_5419443714917073_success_7prhvch9y =
    new actionTypes.EventHandlerImpl({
      action: act_5419443714917073_closeModal_flsr0wuz8,
      event: 'success',
      displayName: 'closeModal',
    });
  const inst_evh_5419443714917073_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5419443714917073_success_7prhvch9y],
    event: 'success',
  });
  const act_2167826738365259_closeModal_7ynx7vlg3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_7ynx7vlg3_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_mp1jotru0 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_7ynx7vlg3,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_mp1jotru0],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_pwu87a2ns,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_l4093wc4j,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_lcsx4xbjk,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_31vnplytf,
    inst_evh_1040167445267876_submit_k4lr498x7,
    submit: inst_evh_1040167445267876_submit_k4lr498x7,
    inst_evh_1040167445267876_submit,
    inst_evh_3322283983417540_success_1x9pwdg5v,
    inst_evh_3322283983417540_success,
    inst_evh_5419443714917073_success_7prhvch9y,
    inst_evh_5419443714917073_success,
    inst_evh_2167826738365259_clickPreventStop_mp1jotru0,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_pwu87a2ns = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_pwu87a2ns.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_l4093wc4j = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_l4093wc4j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_lcsx4xbjk = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_lcsx4xbjk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_31vnplytf = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_31vnplytf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_k4lr498x7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_k4lr498x7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success_1x9pwdg5v = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success_1x9pwdg5v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_success = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success_7prhvch9y = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success_7prhvch9y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5419443714917073_success = async (event, scope) => {
    return await actions.inst_evh_5419443714917073_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_mp1jotru0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_mp1jotru0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterConsumption"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :id="formId"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="meter"
          :label="pgettext('MeterConsumption', 'Meter')"
          :helpTooltip="
            pgettext(
              'MeterConsumption',
              'The meter this consumption belongs to'
            )
          "
          :label-cols-md="4"
          :content-cols-md="8"
          required
          :labelAlign="labelAlign"
        >
          <UcMeterSelector
            :item="item"
            path="meter"
            :readonly="isReadOnly"
            required
          >
          </UcMeterSelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="consumer"
          :label="pgettext('MeterConsumption', 'Consumer')"
          :helpTooltip="
            pgettext('MeterConsumption', 'The consumer of the resources')
          "
          :label-cols-md="4"
          :content-cols-md="8"
          required
          :labelAlign="labelAlign"
        >
          <UcCompanySelector
            :item="item"
            path="consumer"
            :readonly="isReadOnly"
            required
          >
          </UcCompanySelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="unit"
          :label="pgettext('MeterConsumption', 'Unit')"
          :helpTooltip="pgettext('MeterConsumption', 'Unit of measurement')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="unit"
            v-model="item.unit"
            :options="units"
            valueField="symbol"
            textField="symbol"
            :readonly="isReadOnly"
            required
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="start"
          :label="pgettext('MeterConsumption', 'Start')"
          :helpTooltip="
            pgettext('MeterConsumption', 'Start reading and it\'s date')
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="row"
          required
        >
          <div class="col">
            <ItpFormInput
              type="number"
              name="start_reading"
              :placeholder="
                pgettext('MeterConsumption|InputPlaceholder', 'Start reading')
              "
              v-model="item.start.reading"
              :readonly="isReadOnly"
            >
            </ItpFormInput>
          </div>
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="start_date"
              :placeholder="
                pgettext('MeterConsumption|InputPlaceholder', 'Start date')
              "
              v-model="item.start.date"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="end"
          :label="pgettext('MeterConsumption', 'End')"
          :helpTooltip="
            pgettext('MeterConsumption', 'End reading and it\'s date')
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="row"
          required
        >
          <div class="col">
            <ItpFormInput
              type="number"
              name="end_reading"
              :placeholder="
                pgettext('MeterConsumption|InputPlaceholder', 'End reading')
              "
              v-model="item.end.reading"
              :readonly="isReadOnly"
            >
            </ItpFormInput>
          </div>
          <div class="col">
            <ItpFormDatetime
              type="date"
              name="end_date"
              :placeholder="
                pgettext('MeterConsumption|InputPlaceholder', 'End date')
              "
              v-model="item.end.date"
              :readonly="isReadOnly"
            >
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="rental"
          :label="pgettext('MeterConsumption', 'Rental')"
          :helpTooltip="
            pgettext(
              'MeterConsumption',
              'The rental the consumption belongs to'
            )
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormSearchableSelect
            name="rental"
            v-model="item.rental"
            :dataSource="b_4152404563918957_dataSource"
            valueField="id"
            textField="name"
            bind-object
            clearable
            use-option-template-for-selected-option
            optionTemplate='<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>'
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="non_billable"
          :label="pgettext('MeterConsumption', 'Non billable')"
          :helpTooltip="
            pgettext(
              'MeterConsumption',
              'Optional flag to exclude this consumption from billing processes'
            )
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormCheckbox
            name="non_billable"
            inline
            v-model="item.non_billable"
            :readonly="isReadOnly"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_factor"
          :label="pgettext('MeterConsumption', 'Billing factor')"
          :helpTooltip="
            pgettext(
              'MeterConsumption',
              'Optional multiplier applied to consumption'
            )
          "
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormInput
            type="number"
            name="billing_factor"
            v-model="item.billing_factor"
            :readonly="isReadOnly"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff"
          :label="pgettext('MeterConsumption', 'Billing tariff')"
          :helpTooltip="
            pgettext('MeterConsumption', 'Tariff plan to use for billing')
          "
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
        >
          <ItpFormSearchableSelect
            name="tariff"
            v-model="item.tariff"
            :options="tariffs"
            valueField="id"
            textField="name"
            :readonly="isReadOnly"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="unit_price"
          :label="pgettext('MeterConsumption', 'Unit price')"
          :helpTooltip="
            pgettext('MeterConsumption', 'Price per unit for billing')
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          v-if="item.unit_price"
        >
          <ItpFormInput
            :placeholder="
              pgettext('MeterConsumption|InputPlaceholder', 'Amount')
            "
            type="number"
            name="unit_price_amount"
            v-model="item.unit_price.amount"
            class="col-8"
          >
          </ItpFormInput>
          <ItpFormNativeSelect
            :placeholder="
              pgettext('MeterConsumption|InputPlaceholder', 'Currency')
            "
            name="currency"
            v-model="item.unit_price.currency"
            :options="currencies"
            textField="code"
            valueField="code"
            required
            class="col-4"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <hr />
        <ItpFormGroup
          name="note"
          :label="pgettext('MeterConsumption', 'Note')"
          :helpTooltip="
            pgettext(
              'MeterConsumption',
              'Optional note or comment about this meter period'
            )
          "
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="note"
            v-model="item.note"
            :readonly="isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto" v-if="oid && !item.locked">
          <BtnDeleteResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            :tabindex="-1"
            @success="evh_3322283983417540_success($event, {})"
          >
          </BtnDeleteResource>
          <BtnLockResource
            :resource="item"
            :disabled="actions.submit.isRunning"
            :tabindex="-1"
            class="ms-3"
            @success="evh_5419443714917073_success($event, {})"
          >
          </BtnLockResource>
        </ItpBox>
        <ItpButton
          :text="pgettext('ModalButton', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="!item.locked"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('ModalButton', 'Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
